import { useEffect, useRef } from 'react';
import { postMessage } from '../helpers/utils';
import { setupAudioWorklet } from '../lib/vadAudioProcessor';
import { useSimulationContext } from '../providers/SimulationProvider';
import {
  AUDIO_CHUNK_STATUS,
  MESSAGE_EVENT,
  MESSAGE_TYPES,
  VAD_STATES,
  VOICE_MODE_STATUS
} from '../constants/simulationConstants';
import debounce from 'lodash/debounce';

function useVoiceActivityDetection({
  convaiClient,
  clearAudioQueue,
  clearResponseTimeout,
  silenceDurationMs = 2_000,
  standbyTimeoutMs = 20_000
}) {
  const {
    userText,
    isDebugVad,
    isUserInterrupt,
    isVoiceModeActive,
    isAiTalking,
    setIsVoiceModeActive,
    setIsUserInterrupt
  } = useSimulationContext();

  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const startAudioChunkTimerRef = useRef(null); // timer for start audio chunk
  const vadStateRef = useRef(null); // Start in LISTENING state
  const hasUserSpokenRef = useRef(false); // Track user has spoken at least once
  const isVADSetupRef = useRef(false); // Track if VAD is already set up
  const prevIsAiTalkingRef = useRef(false); // Track previous AI playing state to detect transitions
  const lastSendAudioRef = useRef(null); // Track last sent audio chunk status
  const standbyVoiceModeTimerRef = useRef(null); // timer for standby voice mode
  const convaiAudioTeardownUntilRef = useRef(0);  // Convai endAudioChunk stops its recorder after 100ms; defer re-open until then.

  /**
   * isMicMuted (state):
   * - Drives React rendering and useEffect logic
   * - Used for UI and lifecycle-aware behavior
   *
   * isMicMutedRef (ref):
   * - Mirrors isMicMuted for real-time callbacks (audio processing, timers, debounced functions)
   * - Prevents stale state inside callbacks that are created once and do not re-render
   */
  const isMicMutedRef = useRef(false);

  // Enable debug logging for VAD
  const debugVAD = (logMessage, logRef = '') => {
    if (isDebugVad) console.log('[Debug VAD]', logMessage, logRef);
  };

  // Debounced functions for audio chunk start and end
  const debouncedStartAudioChunk = useRef(
    debounce(
      () => {
        debugVAD('Start listening');
        convaiClient.current.startAudioChunk();
        vadStateRef.current = VAD_STATES.AI_LISTENING;
        lastSendAudioRef.current = AUDIO_CHUNK_STATUS.START_AUDIO_CHUNK;
        startStandbyTimer();
      },
      400,
      // First call executes immediately, subsequent calls wait for the delay
      { leading: true, trailing: false }
    )
  ).current;
  const debouncedEndAudioChunk = useRef(
    debounce(
      () => {
        debugVAD('Stop listening');
        const requestData = {
          type: MESSAGE_TYPES.REQUEST_USER_EMOTION,
          payload: { event: MESSAGE_EVENT.USER_EMOTION_LIVE_CHAT }
        };

        postMessage(requestData);
        convaiClient.current.endAudioChunk();
        lastSendAudioRef.current = AUDIO_CHUNK_STATUS.END_AUDIO_CHUNK;
        vadStateRef.current = VAD_STATES.AI_ANALYZING;
      },
      200,
      // First call executes immediately, subsequent calls wait for the delay
      { leading: true, trailing: false }
    )
  ).current;

  const postVADState = (status) => {
    postMessage({
      type: MESSAGE_TYPES.VOICE_MODE,
      payload: {
        status: status
      }
    });
  };

  const cancelDebouncedAudioChunk = () => {
    postVADState(VAD_STATES.IDLE);
    debouncedStartAudioChunk.cancel();
    debouncedEndAudioChunk.cancel();
  };

  /**
   * Closes an open Convai mic session when voice mode shuts down. cleanupVAD
   * only tore down our VAD worklet; the SDK recorder kept streaming and, on
   * re-enable, orphan worklets hit a finished gRPC client.
   */
  const finalizeOpenConvaiAudioChunk = () => {
    debouncedStartAudioChunk.cancel();
    debouncedEndAudioChunk.cancel();

    const hadOpenChunk =
      lastSendAudioRef.current === AUDIO_CHUNK_STATUS.START_AUDIO_CHUNK;

    if (convaiClient.current && hadOpenChunk) {
      try {
        convaiClient.current.endAudioChunk();
        lastSendAudioRef.current = AUDIO_CHUNK_STATUS.END_AUDIO_CHUNK;
        convaiAudioTeardownUntilRef.current = Date.now() + 150;
      } catch (error) {
        console.error(
          '[VAD Error] Error ending audio chunk during cleanup:',
          error
        );
      }
    }

    return hadOpenChunk;
  };

  const abortOpenListeningSession = () => {
    cancelDebouncedAudioChunk();

    if (standbyVoiceModeTimerRef.current) {
      clearTimeout(standbyVoiceModeTimerRef.current);
      standbyVoiceModeTimerRef.current = null;
    }

    silenceTimerRef.current = null;
    hasUserSpokenRef.current = false;
    lastSendAudioRef.current = null;
    vadStateRef.current = VAD_STATES.IDLE;
  };

  const startStandbyTimer = () => {
    // Reset standby timer when voice is detected
    if (standbyVoiceModeTimerRef.current) {
      clearTimeout(standbyVoiceModeTimerRef.current);
      standbyVoiceModeTimerRef.current = null;
    }

    debugVAD('[StandbyMode] Standby timer started ....');

    standbyVoiceModeTimerRef.current = setTimeout(() => {
      debugVAD('[StandbyMode] Standby mode');
      endAIListening();
      clearTimeout(standbyVoiceModeTimerRef.current);
      standbyVoiceModeTimerRef.current = null;
      vadStateRef.current = VAD_STATES.STANDBY_VOICE_MODE;
    }, standbyTimeoutMs);
  };

  // Start listening (open the microphone to Convai)
  const startAIListening = () => {
    const isConvaiTeardownPending =
      Date.now() < convaiAudioTeardownUntilRef.current;

    if (
      !convaiClient.current ||
      lastSendAudioRef.current === AUDIO_CHUNK_STATUS.START_AUDIO_CHUNK ||
      isMicMutedRef.current ||
      isConvaiTeardownPending
    )
      return;

    try {
      clearAudioQueue();
      clearResponseTimeout();
      debouncedEndAudioChunk.cancel();
      debouncedStartAudioChunk();
    } catch (error) {
      console.error('[VAD Error] Error starting audio chunk:', error);
      vadStateRef.current = VAD_STATES.IDLE;
      debouncedStartAudioChunk.cancel();
    }
  };

  // Handle when user stops speaking (after silence timeout)
  const endAIListening = () => {
    if (
      !convaiClient.current ||
      !lastSendAudioRef.current ||
      lastSendAudioRef.current === AUDIO_CHUNK_STATUS.END_AUDIO_CHUNK
    )
      return;

    // Mic opened after AI finished but user never spoke — do not end the Convai
    // chunk (that produces a spurious AI reply), e.g. on voice-mode shutdown.
    if (!hasUserSpokenRef.current) {
      abortOpenListeningSession();
      return;
    }

    try {
      if (standbyVoiceModeTimerRef.current) {
        clearTimeout(standbyVoiceModeTimerRef.current);
        standbyVoiceModeTimerRef.current = null;
      }
      debouncedEndAudioChunk();
    } catch (error) {
      console.error(
        '[VAD Error] Error ending audio chunk (may already be finished):',
        error
      );
      debouncedEndAudioChunk.cancel();
      vadStateRef.current = VAD_STATES.IDLE;
      postVADState(VAD_STATES.IDLE);
    } finally {
      silenceTimerRef.current = null;
      hasUserSpokenRef.current = false;
    }
  };

  const handleVoiceDetected = () => {
    if (isMicMutedRef.current) return;
    if (vadStateRef.current !== VAD_STATES.USER_ACTIVE) {
      vadStateRef.current = VAD_STATES.USER_ACTIVE;
      postVADState(VAD_STATES.USER_ACTIVE);
    }
    if (!hasUserSpokenRef.current) hasUserSpokenRef.current = true;

    debugVAD('Voice detected');

    // Clear any "end of speech" timer since user is still talking
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    // Clear start audio chunk timer
    if (startAudioChunkTimerRef.current) {
      clearTimeout(startAudioChunkTimerRef.current);
      startAudioChunkTimerRef.current = null;
    }

    // Clear standby voice mode timer
    if (standbyVoiceModeTimerRef.current) {
      clearTimeout(standbyVoiceModeTimerRef.current);
      standbyVoiceModeTimerRef.current = null;
    }

    startAIListening();
  };

  const handleVoiceSilence = () => {
    // Only start silence timer if:
    // 1. In LISTENING state
    // 2. User HAS spoken at least once
    // Prevents triggering AI response when mic opens but user hasn't spoken yet

    if (
      !silenceTimerRef.current &&
      hasUserSpokenRef.current &&
      vadStateRef.current === VAD_STATES.USER_ACTIVE
    ) {
      silenceTimerRef.current = setTimeout(() => {
        // User has been quiet long enough, end speech, AI responds
        vadStateRef.current = VAD_STATES.AI_PROCESSING;
        postVADState(VAD_STATES.AI_ANALYZING);
        endAIListening();
      }, silenceDurationMs);
    }
  };

  // Voice Activity Detection (VAD) to automatically detect when user stops speaking
  const setupVAD = async () => {
    if (isVADSetupRef.current) return;

    try {
      // Microphone access
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Create audio processing pipeline
      // AudioContext to process audio
      audioContextRef.current = new (
        window.AudioContext || window.webkitAudioContext
      )();

      // Connect microphone to our audio processor
      const source = audioContextRef.current.createMediaStreamSource(
        mediaStreamRef.current
      );

      const workletSetup = await setupAudioWorklet(
        audioContextRef.current,
        source
      );

      processorRef.current = workletSetup.node;

      // Handle voice detection messages from AudioWorklet
      // AudioWorklet runs in separate thread for better CPU efficiency
      workletSetup.node.port.onmessage = (event) => {
        if (
          !convaiClient.current ||
          !isVoiceModeActive ||
          !isVADSetupRef.current
        )
          return;

        /**
         * AudioWorklet provides two detection states:
         *  1. voiceDetected: Boolean from temporal smoothing (3-frame window)
         *     Issue: Not reliable - doesn't trigger at start of speech, triggers mid-phrase
         *  2. confidence: Float (0-1) from frequency analysis confidence score
         *     Better: More responsive, triggers immediately on voice onset
         *
         *  Using confidence threshold instead of voiceDetected raw boolean provides faster 
         *  detection and better accuracy for voice onset
  
         **/
        // const { voiceDetected, confidence } = event.data;
        const { confidence } = event.data;

        const isVoiceDetected = confidence > 0.15;

        if (isVoiceDetected) {
          handleVoiceDetected();
        } else {
          handleVoiceSilence();
        }
      };

      // Store cleanup function for later use in cleanupVAD
      audioContextRef.current.vadCleanup = workletSetup.cleanup;

      // Mark VAD as set up
      if (isVoiceModeActive && !isVADSetupRef.current) {
        isVADSetupRef.current = true;
        vadStateRef.current = VAD_STATES.AI_LISTENING;
        const teardownDelay = Math.max(
          0,
          convaiAudioTeardownUntilRef.current - Date.now()
        );
        setTimeout(() => {
          if (!isVADSetupRef.current) return;
          startAIListening();
        }, teardownDelay);
      }
    } catch (error) {
      console.error('[VAD Error] Failed to setup:', error);
      if (error.name === 'NotAllowedError') {
        postMessage({
          type: MESSAGE_TYPES.VOICE_MODE,
          error: 'microphone access denied',
          payload: {
            status: VOICE_MODE_STATUS.INACTIVE
          }
        });
        setIsVoiceModeActive(false);
      }
      isVADSetupRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  // Cleanup VAD resources
  const cleanupVAD = () => {
    finalizeOpenConvaiAudioChunk();
    isVADSetupRef.current = false;
    abortOpenListeningSession();

    setIsUserInterrupt(false);

    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (startAudioChunkTimerRef.current) {
      clearTimeout(startAudioChunkTimerRef.current);
      startAudioChunkTimerRef.current = null;
    }

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.port.close?.();
      processorRef.current = null;
    }

    // Cleanup AudioWorklet resources
    if (audioContextRef.current?.vadCleanup) {
      audioContextRef.current.vadCleanup();
      audioContextRef.current.vadCleanup = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      mediaStreamRef.current = null;
    }
    if (isMicMutedRef.current) isMicMutedRef.current = false;

    vadStateRef.current = VAD_STATES.IDLE;
    lastSendAudioRef.current = null;

    debugVAD('[Cleanup] cleaned up');
  };

  useEffect(() => {
    if (!isVoiceModeActive || userText) return;
    if (!prevIsAiTalkingRef.current && !isMicMutedRef.current)
      postVADState(VAD_STATES.IDLE);

    setupVAD();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userText, convaiClient, isVoiceModeActive]);

  useEffect(() => {
    if (!isVoiceModeActive) return;
    if (isUserInterrupt) {
      isMicMutedRef.current = false;
      vadStateRef.current = VAD_STATES.AI_LISTENING;
      startAIListening();
      setIsUserInterrupt(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVoiceModeActive, isUserInterrupt]);

  // Monitor AI playback state and UI transitions
  useEffect(() => {
    if (!isVoiceModeActive) {
      cleanupVAD();
      return;
    }

    const previousIsAiTalking = prevIsAiTalkingRef.current;

    // Detect transitions only (not just current state)
    if (!previousIsAiTalking && isAiTalking) {
      vadStateRef.current = VAD_STATES.AI_ACTIVE;
      postVADState(VAD_STATES.AI_ACTIVE);
      prevIsAiTalkingRef.current = isAiTalking;
    } else if (previousIsAiTalking && !isAiTalking) {
      isMicMutedRef.current = false;
      vadStateRef.current = VAD_STATES.AI_LISTENING;
      postVADState(VAD_STATES.IDLE);
      prevIsAiTalkingRef.current = isAiTalking;
      startAIListening();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vadStateRef, isAiTalking, isVoiceModeActive, prevIsAiTalkingRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupVAD();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    vadStateRef,
    isMicMutedRef,
    postVADState,
    startAIListening,
    cancelDebouncedAudioChunk
  };
}

export default useVoiceActivityDetection;
